import { APP_SCORING_FORMAT } from '@/constants/config';
import { formatScore, getSkillLevel } from '@/domains';
import { PassFailTag, Tag } from '@/components/Tags';
import { SkeletonLoader } from '@/components/Loaders';
import { TSimulationType } from '@/types/simulation';

/**
 * RoleplayCardHistoryItemScoreTag component is responsible for rendering the
 * appropriate score tag based on the simulation type and scoring format. It
 * handles both loading states and the logic for determining pass/fail for
 * practice and certification simulations.
 */
export const RoleplayCardHistoryItemScoreTag: React.FC<{
  simulationType?: TSimulationType;
  isLoading?: boolean;
  pass?: boolean;
  score: number;
  scoreMax?: number;
}> = ({ simulationType, isLoading, pass, score, scoreMax }) => {
  // ============================
  // Loading State
  // ============================
  if (isLoading) {
    return <SkeletonLoader rounded="md" className="h-[24px] w-[34px]" />;
  }

  /**
   * Determines the appropriate score tag to display based on the
   * app's scoring format and simulation type.
   */
  const RoleplayScoreTagProvider = () => {
    const v = getSkillLevel(score);
    switch (APP_SCORING_FORMAT) {
      case 'numerical':
        return <Tag label={formatScore(score, scoreMax, 'numerical')} variant={v} />;
      case 'numerical clamp':
        return <Tag label={formatScore(score, scoreMax, 'numerical clamp')} variant={v} />;
      case 'numerical grade':
        return <Tag label={formatScore(score, scoreMax, 'numerical grade')} variant={v} />;
      case 'numerical round':
        return <Tag label={formatScore(score, scoreMax, 'numerical round')} variant={v} />;
      case 'percentage':
        return <Tag label={formatScore(score, scoreMax, 'percentage')} variant={v} />;
      default:
        return <Tag label={score.toString()} variant={v} />;
    }
  };

  // ============================
  // Render
  // ============================
  switch (simulationType) {
    case 'certification':
      return (
        <PassFailTag
          showIcon={simulationType === 'certification'}
          simulationType={simulationType}
          variant={pass ? 'pass' : 'fail'}
        />
      );
    case 'practice':
      return (
        <PassFailTag
          showIcon={simulationType === 'practice'}
          simulationType={simulationType}
          variant={pass ? 'pass' : 'fail'}
        />
      );
    case 'role-play':
    default:
      return <RoleplayScoreTagProvider />;
  }
};
